import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  IndianRupee, Landmark, ReceiptText, Wallet, AlertCircle, FileText, Plus,
} from 'lucide-react';
import { accountsAPI, ExpenseReportResponse } from '../../services/api';
import { currentFY, fyOptions, FY_MONTHS } from './fy';

const inr = (n: number) =>
  n.toLocaleString('en-IN', { maximumFractionDigits: 2 });

const dmy = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const ExpensesTab: React.FC = () => {
  const [fy, setFy] = useState(currentFY());
  const [month, setMonth] = useState<number | ''>('');
  const [data, setData] = useState<ExpenseReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await accountsAPI.getExpenses(fy, month === '' ? null : month);
      setData(res.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load expenses');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [fy, month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const rows = data?.rows ?? [];
  const summary = data?.summary;

  const summaryCards = summary ? [
    { label: 'Total Expenses', value: `₹${inr(summary.totalAmount)}`, icon: IndianRupee, tone: 'from-rose-500 to-pink-600' },
    { label: 'Tax Paid', value: `₹${inr(summary.totalTax)}`, icon: Landmark, tone: 'from-amber-500 to-orange-500' },
    { label: 'TDS Deducted', value: `₹${inr(summary.totalTds)}`, icon: ReceiptText, tone: 'from-sky-500 to-indigo-500' },
    { label: 'Bills', value: String(summary.totalExpenses), icon: Wallet, tone: 'from-slate-500 to-slate-600' },
  ] : [];

  return (
    <div className="space-y-6">
      {/* Filters + add */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={fy}
          onChange={(e) => setFy(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          {fyOptions().map((o) => <option key={o} value={o}>FY {o}</option>)}
        </select>
        <select
          value={month}
          onChange={(e) => setMonth(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
          className="px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All months</option>
          {FY_MONTHS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <Link
          to="/expenses"
          className="ml-auto flex items-center space-x-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm font-semibold shadow-lg shadow-indigo-500/30 hover:opacity-90 transition-opacity"
        >
          <Plus className="h-4 w-4" />
          <span>Add / Manage Bills</span>
        </Link>
      </div>

      {error && (
        <div className="flex items-center space-x-2 bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex items-center space-x-3 min-w-0">
              <div className={`shrink-0 p-2.5 rounded-xl bg-gradient-to-br ${c.tone} shadow-md`}>
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide truncate">{c.label}</div>
                <div className="text-lg lg:text-xl font-bold text-slate-900 truncate">{c.value}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-3 border-indigo-200 border-t-indigo-600" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center text-slate-500">
            No expenses found for FY {fy}{month !== '' ? ` — ${FY_MONTHS.find((m) => m.value === month)?.label}` : ''}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  <th className="px-4 py-3 whitespace-nowrap">Bill No.</th>
                  <th className="px-4 py-3 whitespace-nowrap">Bill Date</th>
                  <th className="px-4 py-3 whitespace-nowrap">Vendor</th>
                  <th className="px-4 py-3 whitespace-nowrap">GST Details</th>
                  <th className="px-4 py-3 whitespace-nowrap">Description</th>
                  <th className="px-4 py-3 whitespace-nowrap">Category</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Taxable Amount</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Tax Amount</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">TDS Deducted</th>
                  <th className="px-4 py-3 whitespace-nowrap text-right">Total Amount</th>
                  <th className="px-4 py-3 whitespace-nowrap">Bill</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.expenseId} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap">{r.billNumber}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{dmy(r.billDate)}</td>
                    <td className="px-4 py-3 text-slate-900 whitespace-nowrap">{r.vendorName}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.vendorGstin || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.description || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{r.category || '—'}</td>
                    <td className="px-4 py-3 text-right text-slate-900 whitespace-nowrap">₹{inr(r.taxableAmount)}</td>
                    <td className="px-4 py-3 text-right text-slate-900 whitespace-nowrap">₹{inr(r.taxAmount)}</td>
                    <td className="px-4 py-3 text-right text-slate-900 whitespace-nowrap">{r.tdsDeducted ? `₹${inr(r.tdsDeducted)}` : '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900 whitespace-nowrap">₹{inr(r.totalAmount)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {r.billFileUrl ? (
                        <a href={r.billFileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1 text-indigo-600 hover:text-indigo-800 font-medium">
                          <FileText className="h-4 w-4" /><span>View</span>
                        </a>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              {summary && (
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200 font-semibold text-slate-900">
                    <td className="px-4 py-3" colSpan={6}>
                      Totals ({summary.totalExpenses} bills)
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹{inr(summary.totalTaxable)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹{inr(summary.totalTax)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹{inr(summary.totalTds)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">₹{inr(summary.totalAmount)}</td>
                    <td className="px-4 py-3" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpensesTab;
